import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { GraduationCap, BookOpen, ArrowRight, Scroll, Sparkles, Compass } from "lucide-react";
import BottomNav from "@/components/site/BottomNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICON = {
  Prophecy: Compass,
  Discipleship: BookOpen,
  Foundations: Scroll,
};

const CATEGORY_TINT = {
  Prophecy: "var(--gold-500)",
  Discipleship: "var(--navy-700)",
  Foundations: "var(--navy-800)",
};

export default function BibleStudies() {
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    axios.get(`${API}/bible-studies`).then((r) => setStudies(r.data || [])).catch(() => {});
  }, []);

  const grouped = studies.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  return (
    <div data-testid="bible-studies-page">
      {/* HERO — book on lectern feel */}
      <section className="page-hero">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(120deg, var(--navy-900), var(--navy-700) 70%, var(--gold-600))",
          }}
        />
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.32em] text-gold-light mb-4">Study Center</div>
            <h1 className="font-display text-4xl lg:text-6xl text-cream leading-tight">
              Open the Word. <span className="italic text-gold-light">Discover Jesus.</span>
            </h1>
            <p className="mt-5 text-cream/85 text-lg leading-relaxed max-w-2xl">
              Verse-by-verse Bible studies on prophecy, Christian living, Sabbath truths, and the foundations of faith — all
              pointing to Christ.
            </p>
          </div>
          <div className="lg:col-span-2 hidden lg:flex justify-center">
            <div className="relative">
              <div className="w-48 h-48 rounded-full bg-cream/95 border-4 border-gold flex items-center justify-center shadow-2xl">
                <GraduationCap size={88} className="text-navy-deep" strokeWidth={1.3} />
              </div>
              <div className="absolute -top-3 -right-3 px-4 py-1.5 rounded-full bg-gold text-navy-deep text-xs font-semibold uppercase tracking-wider shadow">
                {studies.reduce((n, s) => n + (s.lessons || 0), 0)} lessons
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <section className="py-12 bg-white border-y border-cream-200">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <p className="scripture text-xl lg:text-2xl text-navy-deep">
            "Study to shew thyself approved unto God, a workman that needeth not to be ashamed, rightly dividing the word of truth."
          </p>
          <p className="mt-3 text-gold text-xs tracking-[0.3em] uppercase">— 2 Timothy 2:15</p>
        </div>
      </section>

      {/* STUDIES BY CATEGORY */}
      <section className="py-20 lg:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 space-y-16">
          {Object.entries(grouped).map(([category, list]) => {
            const Icon = CATEGORY_ICON[category] || BookOpen;
            return (
              <div key={category} data-testid={`category-${category.toLowerCase()}`}>
                <div className="flex items-center gap-4 mb-8">
                  <span
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: CATEGORY_TINT[category], color: "var(--cream-50)" }}
                  >
                    <Icon size={22} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">{category}</div>
                    <h2 className="font-display text-2xl lg:text-3xl text-navy-deep">{category} Studies</h2>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map((s) => (
                    <article
                      key={s.slug}
                      data-testid={`study-card-${s.slug}`}
                      className="lift relative bg-white rounded-2xl border border-cream-200 hover:border-gold p-7 flex flex-col overflow-hidden"
                    >
                      <span className="chapter-num absolute -top-2 right-4">{s.lessons}</span>
                      <h3 className="font-display text-2xl text-navy-deep">{s.title}</h3>
                      <p className="mt-3 text-navy/70 text-sm leading-relaxed flex-1">{s.summary}</p>
                      <div className="mt-5 flex items-center justify-between">
                        <span className="text-xs text-navy/55">{s.lessons} lessons</span>
                        <Link
                          to="/contact"
                          data-testid={`start-study-${s.slug}`}
                          className="btn-gold px-4 py-2 rounded-full text-sm inline-flex items-center gap-1.5"
                        >
                          Start Study <ArrowRight size={14} />
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-16" style={{ backgroundColor: "var(--navy-900)" }}>
        <div className="max-w-4xl mx-auto px-5 text-center text-cream">
          <Sparkles className="text-gold mx-auto mb-4" size={32} />
          <h3 className="font-display text-2xl lg:text-3xl">Want a study by email?</h3>
          <p className="mt-3 text-cream/80">We'll send you our weekly Bible study direct to your inbox.</p>
          <Link to="/contact" className="mt-6 inline-block btn-gold px-7 py-3 rounded-full" data-testid="studies-cta-contact">
            Request a Study
          </Link>
        </div>
      </section>

      <BottomNav exclude={["/bible-studies"]} />
    </div>
  );
}
